<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import BarChart from 'shared/components/charts/BarChart.vue';
import {
  buildScoreBins,
  relativeBinLevels,
  binDensityKey,
  clampScore,
  scoreBinIndex,
} from './buzzScoreDistribution';
import { DEFAULT_BAR_CHART, DEFAULT_CHART } from '../constants';

const props = defineProps({
  score: {
    type: Number,
    default: null,
  },
  peerScores: {
    type: Array,
    default: () => [],
  },
});

const { t } = useI18n();

const inactiveBarColor = 'rgb(148, 163, 184)';

const bins = computed(() =>
  relativeBinLevels(buildScoreBins(props.peerScores))
);
const activeBin = computed(() => scoreBinIndex(clampScore(props.score)));

const collection = computed(() => ({
  labels: bins.value.map(bin => bin.label),
  datasets: [
    {
      ...DEFAULT_BAR_CHART,
      data: bins.value.map(bin => bin.relativeHeight),
      backgroundColor: bins.value.map((bin, index) =>
        index === activeBin.value
          ? DEFAULT_BAR_CHART.backgroundColor
          : inactiveBarColor
      ),
      borderRadius: 4,
    },
  ],
}));

const tooltipLabel = (bin, index) => {
  if (index === activeBin.value) {
    return t('BUZZ_REPORTS.DISTRIBUTION.TOOLTIP_YOURS');
  }
  const key = binDensityKey(bin);
  return t(`BUZZ_REPORTS.DISTRIBUTION.TOOLTIP_${key}`);
};

const chartOptions = computed(() => ({
  plugins: {
    tooltip: {
      callbacks: {
        label: context =>
          tooltipLabel(bins.value[context.dataIndex], context.dataIndex),
      },
    },
  },
  scales: {
    ...DEFAULT_CHART.scales,
    x: {
      ...DEFAULT_CHART.scales.x,
      title: {
        display: true,
        text: t('BUZZ_REPORTS.DISTRIBUTION.SCORE_RANGE'),
        font: { family: DEFAULT_CHART.scales.x.ticks.fontFamily },
      },
    },
    y: {
      ...DEFAULT_CHART.scales.y,
      min: 0,
      max: 100,
      title: {
        display: true,
        text: t('BUZZ_REPORTS.DISTRIBUTION.RELATIVE'),
        font: { family: DEFAULT_CHART.scales.y.ticks.fontFamily },
      },
      ticks: {
        ...DEFAULT_CHART.scales.y.ticks,
        stepSize: 50,
        callback: value => {
          if (value === 0) return t('BUZZ_REPORTS.DISTRIBUTION.FEWER');
          if (value === 100) return t('BUZZ_REPORTS.DISTRIBUTION.MORE');
          return '';
        },
      },
    },
  },
}));

const hasData = computed(() =>
  props.peerScores.some(value => value != null && !Number.isNaN(Number(value)))
);
</script>

<template>
  <div v-if="!hasData" class="text-sm text-n-slate-10">
    {{ t('BUZZ_REPORTS.DISTRIBUTION.NO_DATA') }}
  </div>
  <div v-else class="h-48">
    <BarChart :collection="collection" :chart-options="chartOptions" />
  </div>
</template>
